import { useEffect } from 'react';
import { shallowEqual } from 'react-redux';
import { createKeybindingsHandler } from 'tinykeys';
import { analytics } from 'app/utils/segment';
import { commandPubSubEventNamePrefix, useCommands, } from 'utils/commands';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { isAppleDevice } from 'utils/keyboard';
import { pubSub } from 'utils/pubSub';
import { useAppSelector } from 'utils/redux/useAppSelector';
export function useCommandKeybindings() {
    var _a;
    const { newKeybindings } = useFeatureFlag(['new-keybindings']);
    const { keybindings: commandsKeybindings, enrichedCommands } = useCommands();
    const { keyboardShortcuts, modals } = useAppSelector((state) => ({
        keyboardShortcuts: state.settings.personal.keyboardShortcuts,
        modals: state.app.modals,
    }), shallowEqual);
    const isRecordingModalVisible = !!(((_a = modals[0]) === null || _a === void 0 ? void 0 : _a.type) === 'app:keyCombinationRecorder');
    const canBind = newKeybindings && keyboardShortcuts;
    useEffect(() => {
        if (!canBind) {
            return;
        }
        const keyBindingMap = enrichedCommands
            .filter(({ isUserDefinedKeybindings, conflicts, command }) => {
            var _a;
            return !command.isExternal &&
                (isUserDefinedKeybindings ||
                    (!isUserDefinedKeybindings && conflicts.length === 0)) &&
                !!((_a = command.keyboard) === null || _a === void 0 ? void 0 : _a.keybindings.length);
        })
            .reduce((keyBindingsMap, { command }) => {
            var _a;
            const { name: commandName, keyboard } = command;
            const commandKeyBindingsMap = Object.fromEntries(((_a = keyboard === null || keyboard === void 0 ? void 0 : keyboard.keybindings) !== null && _a !== void 0 ? _a : []).map((keybinding) => {
                const { binding, presentation } = keybinding;
                const keyCombination = binding.flat().join('+');
                const mods = binding[0];
                const handler = (event) => {
                    const isContentEditableElement = event.target instanceof HTMLElement &&
                        event.target.isContentEditable;
                    const isTargetAnInput = isContentEditableElement ||
                        event.target instanceof HTMLInputElement ||
                        event.target instanceof HTMLTextAreaElement;
                    const isModsOnlyShift = mods.length === 1 && mods.includes('Shift');
                    const isModsOptionAndShiftOnMac = isAppleDevice &&
                        mods.length === 2 &&
                        mods.includes('Alt') &&
                        mods.includes('Shift');
                    const isModsOnlyOptionOnMac = isAppleDevice && mods.length === 1 && mods.includes('Alt');
                    const isKeybindingPreventingFromTyping = mods.length === 0 ||
                        isModsOnlyShift ||
                        isModsOptionAndShiftOnMac ||
                        isModsOnlyOptionOnMac;
                    if ((isKeybindingPreventingFromTyping && isTargetAnInput) ||
                        isRecordingModalVisible) {
                        return;
                    }
                    event.preventDefault();
                    analytics.commandExecuted({
                        commandName,
                        location: 'keyboard',
                        keybinding: presentation
                            ? `${presentation.flat().join('+')} (${keyCombination})`
                            : keyCombination,
                    });
                    pubSub.publish(`${commandPubSubEventNamePrefix}${commandName}`);
                };
                return [keyCombination, handler];
            }));
            return Object.assign(keyBindingsMap, commandKeyBindingsMap);
        }, {});
        const keybindingsHandler = createKeybindingsHandler(keyBindingMap);
        addEventListener('keydown', keybindingsHandler);
        return () => {
            removeEventListener('keydown', keybindingsHandler);
        };
    }, [canBind, commandsKeybindings, enrichedCommands, isRecordingModalVisible]);
}
